import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useGetOnboardingSteps, { OnboardingStep } from '../../apiHooks/queries/onboarding/useGetOnboardingSteps';
import DomainStep from './steps/DomainStep';
import IntegrationStep from './steps/IntegrationStep';
import TrackingStep from './steps/TrackingStep';
import VerifyStep from './steps/VerifyStep';
import CompleteStep from './steps/CompleteStep';

type StepName = 'domain' | 'integration' | 'tracking' | 'verify' | 'complete';

const STEPS: StepName[] = ['domain', 'integration', 'tracking', 'verify', 'complete'];

const resolveActiveStep = (steps: OnboardingStep[]): StepName => {
  const inProgress = steps.find((s) => s.status === 'in_progress');
  if (inProgress) return inProgress.step as StepName;

  const firstPending = steps.find((s) => s.status === 'pending');
  if (firstPending) return firstPending.step as StepName;

  return 'complete';
};

const Setup: FC = () => {
  const { t } = useTranslation();
  const { data, isLoading } = useGetOnboardingSteps();

  const steps = data?.data?.data ?? [];
  const activeStep = useMemo(() => resolveActiveStep(steps), [steps]);
  const activeIndex = STEPS.indexOf(activeStep);

  // Each step handles its own PATCH + invalidates the query.
  // goNext is a no-op here — the re-fetch from invalidation drives the stepper forward.
  const goNext = () => {};

  const renderStep = () => {
    switch (activeStep) {
      case 'domain': return <DomainStep onContinue={goNext} />;
      case 'integration': return <IntegrationStep onContinue={goNext} />;
      case 'tracking': return <TrackingStep onContinue={goNext} />;
      case 'verify': return <VerifyStep onContinue={goNext} />;
      case 'complete': return <CompleteStep onFinish={goNext} />;
      default: return null;
    }
  };

  return (
    <div className="-mx-6 -mt-6 flex min-h-[calc(100vh-64px)] flex-col items-center bg-gray-50 px-4 pt-10">
      {/* Stepper */}
      <div className="mb-10 flex w-full max-w-2xl items-center">
        {STEPS.map((step, index) => {
          const isDone = !isLoading && index < activeIndex;
          const isCurrent = !isLoading && index === activeIndex;
          const isNext = !isLoading && index > activeIndex;

          return (
            <React.Fragment key={step}>
              <div className="flex flex-col items-center gap-2">
                <div
                  className={`
                    flex h-11 w-11 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors
                    ${isLoading ? 'border-gray-200 bg-gray-100 animate-pulse' : ''}
                    ${isDone ? 'border-green text-gray-500' : ''}
                    ${isCurrent ? 'border-yellow bg-yellow text-white animate-step-pulse' : ''}
                    ${isNext ? 'border-red text-gray-500' : ''}
                  `}
                >
                  {!isLoading && isDone ? (
                    <svg width="15" height="15" viewBox="0 0 14 14" fill="none">
                      <path d="M2 7l3.5 3.5L12 4" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    !isLoading && (index + 1)
                  )}
                </div>
                <div className={`h-3 rounded ${isLoading ? 'w-12 bg-gray-200 animate-pulse' : ''}`}>
                  {!isLoading && (
                    <span className="text-xs font-medium capitalize text-gray-400">
                      {t(`setup.step.${step}`)}
                    </span>
                  )}
                </div>
              </div>

              {index < STEPS.length - 1 && (
                <div className="mb-5 mx-2 flex-1">
                  <div
                    className={`h-px w-full transition-colors ${
                      isLoading ? 'bg-gray-200 animate-pulse' : index < activeIndex ? 'bg-green' : 'bg-gray-200'
                    }`}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Step content */}
      {isLoading ? (
        <div className="flex flex-col items-center gap-3 pt-8">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-primary" />
          <p className="text-sm text-gray-400">{t('setup.loading')}</p>
        </div>
      ) : (
        renderStep()
      )}
    </div>
  );
};

export default Setup;
